import React from "react";
import "@fontsource/rajdhani/400.css";
import "@fontsource/rajdhani/600.css";
import "@fontsource/rajdhani/700.css";

import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  SimpleGrid,
  Image,
  Link,
  Flex,
  Divider,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";

const Section = ({ id, title, children }) => (
  <Box id={id} scrollMarginTop="120px">
    <Heading
      fontFamily="'Rajdhani', sans-serif"
      textTransform="uppercase"
      fontSize={{ base: "3xl", md: "4xl" }}
      mb={4}
      color="white"
      letterSpacing="0.04em"
    >
      {title}
    </Heading>

    <Box color="gray.300" fontSize="md" lineHeight="1.8">
      {children}
    </Box>
  </Box>
);

const Fact = ({ label, children }) => (
  <Box>
    <Text
      fontFamily="'Rajdhani', sans-serif"
      fontWeight="700"
      textTransform="uppercase"
      color="white"
      fontSize="lg"
      mb={1}
    >
      {label}
    </Text>

    <Box color="gray.400" fontSize="sm" lineHeight="1.6">
      {children}
    </Box>
  </Box>
);

const PressImage = ({ src, alt }) => (
  <Box
    borderRadius="14px"
    overflow="hidden"
    border="1px solid rgba(255,255,255,0.1)"
    bg="rgba(255,255,255,0.04)"
    transition="0.2s ease"
    _hover={{
      transform: "translateY(-3px)",
      borderColor: "rgba(255,255,255,0.25)",
    }}
  >
    <Image src={src} alt={alt} width="100%" display="block" />
  </Box>
);

const PressKit = () => {
  const scrollToSection = (id) => {
    const section = document.querySelector(id);

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const sidebarLinks = [
    ["Factsheet", "#factsheet"],
    ["Description", "#description"],
    ["History", "#history"],
    ["Features", "#features"],
    ["Videos", "#videos"],
    ["Images", "#images"],
    ["Logo & Icon", "#logo"],
    ["Additional Info", "#additional-info"],
  ];

  return (
    <Box bg="#08090C" color="white" minH="100vh">
      {/* HERO HEADER */}
      <Box
        position="relative"
        py={{ base: 20, md: 28 }}
        // backgroundImage="url('/images/HighresScreenshot00003.png')"
        backgroundSize="cover"
        backgroundPosition="center"
      >
        <Box position="absolute" inset={0} bg="rgba(0,0,0,0.72)" />

        <Container maxW="1200px" position="relative" zIndex={1}>
          <Stack spacing={5}>
            <Text
              fontSize="sm"
              letterSpacing="0.25em"
              textTransform="uppercase"
              color="gray.400"
            >
              Official Press Kit
            </Text>

            <Heading
              fontFamily="'Rajdhani', sans-serif"
              fontSize={{ base: "5xl", md: "8xl" }}
              lineHeight="0.9"
              textTransform="uppercase"
              letterSpacing="0.04em"
            >
              Velocity Protocol
            </Heading>

            <Text
              maxW="680px"
              color="gray.300"
              fontSize={{ base: "md", md: "xl" }}
            >
              A fast-paced rogue-lite movement-shooter built around speed,
              momentum, weapon unlocks, and aggressive sci-fi combat.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* MAIN PRESS KIT LAYOUT */}
      <Container maxW="1200px" py={16}>
        <Flex
          align="flex-start"
          gap={{ base: 10, lg: 16 }}
          direction={{ base: "column", lg: "row" }}
        >
          {/* SIDEBAR */}
          <Box
            width={{ base: "100%", lg: "260px" }}
            flexShrink={0}
            position={{ base: "static", lg: "sticky" }}
            top="110px"
          >
            <Box
              bg="rgba(255,255,255,0.035)"
              border="1px solid rgba(255,255,255,0.08)"
              borderRadius="20px"
              p={6}
            >
              <Heading
                fontFamily="'Rajdhani', sans-serif"
                fontSize="3xl"
                textTransform="uppercase"
                mb={6}
              >
                Press Kit
              </Heading>

              <Stack spacing={3}>
                {sidebarLinks.map(([label, href]) => (
                  <Link
                    key={href}
                    as="button"
                    type="button"
                    onClick={() => scrollToSection(href)}
                    color="gray.400"
                    fontSize="sm"
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                    textAlign="left"
                    _hover={{ color: "white", textDecoration: "none" }}
                  >
                    {label}
                  </Link>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* CONTENT */}
          <Box flex="1" minW={0}>
            <Stack spacing={14}>
              {/* FACTSHEET */}
              <Section id="factsheet" title="Factsheet">
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                  <Fact label="Developer">
                    764 Entertainment
                    <br />
                    Based in the United States
                  </Fact>

                  <Fact label="Release Date">
                    Demo: LIVE NOW
                    <br />
                    Early Access: TBA 2026
                  </Fact>

                  <Fact label="Platforms">
                    PC / Windows
                  </Fact>

                  <Fact label="Website">
                    <Link
                      color="cyan.300"
                      href="https://playvelocityprotocol.com"
                      isExternal
                    >
                      playvelocityprotocol.com
                    </Link>
                  </Fact>

                  <Fact label="Genre">
                    Movement Shooter
                    <br />
                    Roguelike FPS
                  </Fact>

                  <Fact label="Press / Business Contact">
                    <Link
                      color="cyan.300"
                      href="mailto:hello@764entertainment.com"
                    >
                      hello@764entertainment.com
                    </Link>
                  </Fact>
                </SimpleGrid>
              </Section>

              <Divider borderColor="whiteAlpha.200" />

              {/* DESCRIPTION */}
              <Section id="description" title="Description">
                <Text>
                  <strong>Velocity Protocol</strong> is a high-speed first-person roguelike
                  movement-shooter where speed is your strongest
                  weapon. Chain together wallruns, slides, double jumps, and
                  air-strafes while fighting through hostile sci-fi arenas filled
                  with enemies, weapons, upgrades, and randomized combat
                  encounters.
                </Text>
              </Section>

              {/* HISTORY */}
              <Section id="history" title="History">
                <Stack spacing={5}>
                  <Text>
                    Velocity Protocol began as an experiment in building a fast,
                    responsive parkour FPS movement system inside Unreal Engine.
                    The project grew from a movement prototype into a roguelike
                    shooter focused on replayable runs, weapon progression,
                    biome-based combat, and leaderboard competition.
                  </Text>

                  <Text>
                    The game is designed around the feeling of keeping momentum
                    through combat. Instead of separating traversal and shooting,
                    Velocity Protocol pushes the player to use movement as part
                    of the core combat loop.
                  </Text>
                </Stack>
              </Section>

              {/* FEATURES */}
              <Section id="features" title="Features">
                <UnorderedList spacing={3}>
                  <ListItem>
                    Fast movement system with wallrunning, sliding, double
                    jumping, and air-strafing.
                  </ListItem>

                  <ListItem>
                    Roguelike FPS structure with randomized rooms, enemies,
                    weapons, and upgrades.
                  </ListItem>

                  <ListItem>
                    Weapon unlocks and progression designed to reward repeated
                    runs.
                  </ListItem>

                  <ListItem>
                    Stylized sci-fi visuals focused on clarity, speed, and
                    readable combat.
                  </ListItem>

                  <ListItem>
                    Leaderboard support for best times and competitive
                    replayability.
                  </ListItem>
                </UnorderedList>
              </Section>

              {/* VIDEOS */}
              <Section id="videos" title="Videos">
                <Stack spacing={5}>
                  <Heading
                    fontFamily="'Rajdhani', sans-serif"
                    textTransform="uppercase"
                    fontSize="2xl"
                  >
                    Trailer
                  </Heading>

                  <Box
                    aspectRatio={16 / 9}
                    bg="black"
                    borderRadius="20px"
                    overflow="hidden"
                    border="1px solid rgba(255,255,255,0.1)"
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/vFbwIWYkDDs"
                      title="Velocity Protocol Trailer"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{ border: "none" }}
                    />
                  </Box>
                </Stack>
              </Section>

              {/* IMAGES */}
              <Section id="images" title="Images">
                <Stack spacing={5}>
                  <Text color="gray.400">
                    Screenshots and GIFs may be used for coverage, previews,
                    articles, and video content.
                  </Text>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
                    <PressImage
                      src="/images/GameplayGIF2.gif"
                      alt="Velocity Protocol Gameplay Combat GIF"
                    />

                    <PressImage
                      src="/images/HighresScreenshot00005.png"
                      alt="Velocity Protocol Desert Demo Map Screenshot"
                    />

                    <PressImage
                      src="/images/GameplayScreenshot3.png"
                      alt="Velocity Protocol Rocket Launcher Gameplay Screenshot"
                    />

                    <PressImage
                      src="/images/HighresScreenshot00002.png"
                      alt="Velocity Protocol Cinematic Screenshot"
                    />

                    <PressImage
                      src="/images/GameplayScreenshot2.png"
                      alt="Velocity Protocol Movement Shooter Screenshot"
                    />

                    <PressImage
                      src="/images/GameplayScreenshot.png"
                      alt="Velocity Protocol Wall Run Combat Shotgun Screenshot "
                    />

                    <PressImage
                      src="/images/GameplayScreenshot5.png"
                      alt="Velocity Protocol Wall Run Parkour Gameplay ScreenShot"
                    />
                    <PressImage
                      src="/images/gameplayGIF1.gif"
                      alt="Velocity Protocol Fast Paced Wall Run Combat GIF"
                    />
                    <PressImage
                      src="/images/GameplayScreenshot4.png"
                      alt="Velocity Protocol Sliding down slope gameplay screenshot"
                    />
                    <PressImage
                      src="/images/HighresScreenshot00007.png"
                      alt="Velocity Protocol Gameplay Screenshot"
                    />
                    <PressImage
                      src="/images/HighresScreenshot00008.png"
                      alt="Velocity Protocol Enemy Character Showcase"
                    />
                    <PressImage
                      src="/images/HighresScreenshot00011.png"
                      alt="Velocity Protocol by 764 Entertainment"
                    />
                  </SimpleGrid>
                </Stack>
              </Section>

              {/* LOGO */}
              <Section id="logo" title="Logo & Icon">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={5}>


                  
                  <Box
                    bg="rgba(255,255,255,0.04)"
                    border="1px solid rgba(255,255,255,0.08)"
                    borderRadius="20px"
                    p={6}
                  >
                    <Image
                      src="/images/VelocityProtocolLogoPNG.png"
                      alt="Velocity Protocol dark logo"
                    />
                  </Box>

                  
                  <Box
                    bg="rgba(255,255,255,0.04)"
                    border="1px solid rgba(255,255,255,0.08)"
                    borderRadius="20px"
                    p={6}
                  >
                    <Image
                      src="/images/VelocityProtocolLogoPNGWhite.png"
                      alt="Velocity Protocol white logo"
                    />
                  </Box>


                  {/* <Box
                    bg="rgba(255,255,255,0.04)"
                    border="1px solid rgba(255,255,255,0.08)"
                    borderRadius="20px"
                    p={6}
                  >
                    <Image src="/images/icon.png" alt="Velocity Protocol icon" />
                  </Box> */}
                </SimpleGrid>
              </Section>

              {/* ADDITIONAL INFO */}
              <Section id="additional-info" title="Additional Information">
                <Stack spacing={5}>
                  <Fact label="Website">
                    <Link
                      color="cyan.300"
                      href="https://playvelocityprotocol.com"
                      isExternal
                    >
                      playvelocityprotocol.com
                    </Link>
                  </Fact>


                  <Fact label="Email">
                    <Link
                      color="cyan.300"
                      href="mailto:hello@764entertainment.com"
                    >
                      hello@764entertainment.com
                    </Link>
                  </Fact>

                  <Fact label="Developer">
                    <Link
                      color="cyan.300"
                      href="https://764entertainment.com"
                      isExternal
                    >
                      764entertainment.com
                    </Link>
                  </Fact>                  

                  <Fact label="YouTube">
                    <Link
                      color="cyan.300"
                      href="https://www.youtube.com/@764Entertainment"
                      isExternal
                    >
                      youtube.com/@764Entertainment
                    </Link>
                  </Fact>

                  <Fact label="Steam">
                    Steam page coming soon.
                  </Fact>
                </Stack>
              </Section>
            </Stack>
          </Box>
        </Flex>
      </Container>
    </Box>
  );
};

export default PressKit;
